/*
 * Copyright (c) 2026.
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden volgens de voorwaarden die zijn vastgelegd in een bijbehorende schriftelijke overeenkomst met Spin in het Web B.V.
 */

/**
 * Auteur: Jelmer Jellema - Spin in het Web B.V
 * Gegenereerd met Claude Code (AI), 23-09-2026
 *
 * Permanente, minimale root-service rond het simulatiecanvas: het eiland zelf, voor de services die het
 * moeten aansturen. De tegenhanger van `GlobalModelcanvasService`: services kennen deze service, niet
 * het component.
 *
 * Tot golf 4e liep het tekenen van een simulatie langs de AngularJS-simview, die negentien
 * doorgeefluiken naar het eiland had (`publicI('simctrl')`) en zich als `SimviewUi` bij de
 * `SimulatieService` aanmeldde. Die weg is er niet meer: de `SimulatieService` vraagt het eiland hier op.
 */
import {Injectable} from '@angular/core';
import type {SimulatieData} from '../../components/canvas/dl-simcanvas/dl-simcanvas.component';

/**
 * Wat de `SimulatieService` van het simulatiecanvas nodig heeft. Het eiland kan meer (de samenwerkbaan
 * spreekt hem aan als `Simulatie_eiland`); dit is de kant die bij het tekenen en opruimen hoort.
 */
export interface SimcanvasEiland {
    /** Tekent de gegeven simulatie; `fullsim` beeldt hem meteen helemaal af. */
    startSimulatie(simdata: SimulatieData, fullsim: boolean, invoerhash?: string | null): void;

    /** Zet de volgende stap in de getekende simulatie. */
    nextStep(): void;

    /** Legt de toestanden met zo weinig mogelijk kruisende lijnen neer. */
    niceStates(): Promise<void>;

    /**
     * Maakt het canvas leeg. Bewaart de posities voor een identieke herstart en stuurt de nieuwe
     * (lege) stand rond, tenzij `skipSamenwerking`.
     */
    reset(skipSamenwerking?: boolean): void;

    /** Stuurt de huidige stand naar de andere deelnemers; met `fit` vragen ze hun beeld bij te trekken. */
    planStatusVersturen(direct?: boolean, fit?: boolean): void;
}

@Injectable({providedIn: 'root'})
export class GlobalSimcanvasService {

    /**
     * Het aangemelde simcanvas-eiland, of `null` als er (nog) geen simulatiescherm is.
     */
    private eiland: SimcanvasEiland | null = null;

    /**
     * Meldt het eiland aan. Aangeroepen door `DlSimCanvasComponent` bij init.
     */
    setEiland(eiland: SimcanvasEiland): void {
        this.eiland = eiland;
    }

    /**
     * Ruimt de referentie op. De identiteitscheck voorkomt dat een laat-afgebroken oud eiland een
     * inmiddels nieuw aangemeld eiland wist.
     */
    clearEiland(eiland: SimcanvasEiland): void {
        if (this.eiland === eiland) {
            this.eiland = null;
        }
    }

    /**
     * Het aangemelde eiland, of `null`.
     */
    getEiland(): SimcanvasEiland | null {
        return this.eiland;
    }
}
